"use client";

import React, { useState, useEffect } from "react";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  IndianRupee,
  Calendar,
  Download,
  Printer,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  XCircle,
  HelpCircle,
  Phone,
  MessageSquare,
  ArrowRight,
  Filter,
  Layers,
  Award,
  Zap,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/Button";

interface CrmReportData {
  kpis: {
    totalLeads: number;
    activePipelineCount: number;
    activePipelineValueINR: number;
    wonCount: number;
    wonRevenueINR: number;
    lostCount: number;
    lostValueINR: number;
    winRate: number;
    lossRate: number;
    projectedMonthlyRetainerINR: number;
    avgDealSizeINR: number;
  };
  funnel: {
    stage: string;
    label: string;
    count: number;
    value: number;
    percentage: number;
    color: string;
  }[];
  sources: {
    channel: string;
    totalLeads: number;
    wonLeads: number;
    lostLeads: number;
    activeLeads: number;
    wonRevenue: number;
    pipelineValue: number;
    conversionRate: number;
  }[];
  lostDeals: {
    id: number;
    name: string;
    company_name: string | null;
    phone: string | null;
    email: string | null;
    deal_value: number;
    currency: string;
    priority: string;
    last_reason: string;
    lost_at: string;
    bot_paused: number;
  }[];
  tierMix: {
    tier: string;
    name: string;
    count: number;
    value: number;
    retainerValue: number;
    color: string;
  }[];
  velocity: {
    avgDaysToWon: number;
    avgDaysToLost: number;
    recentAudits: {
      id: number;
      lead_id: number;
      lead_name: string;
      action: string;
      old_value: string | null;
      new_value: string | null;
      created_at: string;
    }[];
  };
}

export default function ReportManagementTab() {
  const [report, setReport] = useState<CrmReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<"7DAYS" | "30DAYS" | "THIS_MONTH" | "ALL">("ALL");
  const [leadMode, setLeadMode] = useState<"REAL" | "ALL">("REAL");
  const [restoringId, setRestoringId] = useState<number | null>(null);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange === "7DAYS") {
        const d = new Date(Date.now() - 7 * 86400000).toISOString().split("T")[0];
        params.set("startDate", d);
      } else if (dateRange === "30DAYS") {
        const d = new Date(Date.now() - 30 * 86400000).toISOString().split("T")[0];
        params.set("startDate", d);
      } else if (dateRange === "THIS_MONTH") {
        const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0];
        params.set("startDate", firstDay);
      }
      params.set("lead_mode", leadMode);

      const res = await fetch(`/api/crm/reports?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setReport(data.report);
      }
    } catch (err) {
      console.error("Failed to load CRM reports:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [dateRange, leadMode]);

  const handleRestoreLead = async (leadId: number) => {
    setRestoringId(leadId);
    try {
      const res = await fetch(`/api/crm/leads/${leadId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: "Negotiation",
          priority: "HOT",
        }),
      });
      if (res.ok) {
        await fetchReports();
      }
    } catch (err) {
      console.error("Restore lead error:", err);
    } finally {
      setRestoringId(null);
    }
  };

  const handleExportReportCsv = () => {
    if (!report) return;
    const rows: string[][] = [
      ["Johnson Softwares CRM - Executive Pipeline & Conversion Report"],
      ["Generated At", new Date().toLocaleString("en-IN")],
      ["Date Scope", dateRange],
      ["Lead Mode", leadMode],
      [],
      ["1. EXECUTIVE KPIS"],
      ["Metric", "Value"],
      ["Total Enquiries", String(report.kpis.totalLeads)],
      ["Active Deals in Review", String(report.kpis.activePipelineCount)],
      ["Active Pipeline Value (INR)", `₹${report.kpis.activePipelineValueINR.toLocaleString("en-IN")}`],
      ["Closed Won Count", String(report.kpis.wonCount)],
      ["Closed Won Revenue (INR)", `₹${report.kpis.wonRevenueINR.toLocaleString("en-IN")}`],
      ["Closed Lost Count", String(report.kpis.lostCount)],
      ["Closed Lost Value (INR)", `₹${report.kpis.lostValueINR.toLocaleString("en-IN")}`],
      ["Win Rate (%)", `${report.kpis.winRate}%`],
      ["Loss Rate (%)", `${report.kpis.lossRate}%`],
      ["Projected Monthly Retainer (INR)", `₹${report.kpis.projectedMonthlyRetainerINR.toLocaleString("en-IN")}`],
      [],
      ["2. STAGE FUNNEL BREAKDOWN"],
      ["Stage", "Deals Count", "Value (INR)", "Conversion %"],
      ...report.funnel.map(f => [f.label, String(f.count), `₹${f.value.toLocaleString("en-IN")}`, `${f.percentage}%`]),
      [],
      ["3. ACQUISITION CHANNELS"],
      ["Channel", "Total Leads", "Won", "Lost", "Active", "Won Revenue (INR)", "Win Rate %"],
      ...report.sources.map(s => [s.channel, String(s.totalLeads), String(s.wonLeads), String(s.lostLeads), String(s.activeLeads), `₹${s.wonRevenue.toLocaleString("en-IN")}`, `${s.conversionRate}%`]),
      [],
      ["4. CLOSED LOST AUDIT"],
      ["Lead ID", "Company / Contact", "Phone", "Email", "Lost Value (INR)", "Detected Reason", "Lost Date"],
      ...report.lostDeals.map(d => [String(d.id), d.company_name || d.name, d.phone || "-", d.email || "-", `₹${d.deal_value.toLocaleString("en-IN")}`, d.last_reason, d.lost_at || "-"]),
    ];

    const csvContent = "\uFEFF" + rows.map(e => e.map(cell => `"${(cell || "").replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Johnson_Softwares_CRM_Report_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    }, 60000);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading && !report) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <RefreshCw className="w-8 h-8 animate-spin text-primary mb-3" />
        <p className="text-sm font-medium">Generating Executive Pipeline Analytics...</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-8 text-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
        <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Unable to load report analytics.</p>
        <Button onClick={fetchReports} variant="outline" size="sm" className="mt-4">Retry</Button>
      </div>
    );
  }

  const kpis = report.kpis;

  return (
    <div className="space-y-6 animate-fadeIn pb-12">
      {/* Report Header & Filter Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-xl bg-indigo-500/10 text-indigo-500">
              <BarChart3 className="w-5 h-5" />
            </span>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                Executive Report Management &amp; Pipeline Intelligence
                <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
                  Live Sync
                </span>
              </h2>
              <p className="text-xs text-slate-500">
                End-to-end sales funnel conversion, closed lost diagnostic analysis, and ARR/MRR retainers.
              </p>
            </div>
          </div>
        </div>

        {/* Date Scope & Export Controls */}
        <div className="flex items-center gap-2.5 flex-wrap">
          {/* Date Range Picker */}
          <div className="flex items-center p-1 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs">
            <button
              onClick={() => setDateRange("7DAYS")}
              className={`px-2.5 py-1 rounded-lg font-medium transition-all ${
                dateRange === "7DAYS"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-xs font-bold"
                  : "text-slate-500 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              7 Days
            </button>
            <button
              onClick={() => setDateRange("30DAYS")}
              className={`px-2.5 py-1 rounded-lg font-medium transition-all ${
                dateRange === "30DAYS"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-xs font-bold"
                  : "text-slate-500 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              30 Days
            </button>
            <button
              onClick={() => setDateRange("THIS_MONTH")}
              className={`px-2.5 py-1 rounded-lg font-medium transition-all ${
                dateRange === "THIS_MONTH"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-xs font-bold"
                  : "text-slate-500 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              This Month
            </button>
            <button
              onClick={() => setDateRange("ALL")}
              className={`px-2.5 py-1 rounded-lg font-medium transition-all ${
                dateRange === "ALL"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-xs font-bold"
                  : "text-slate-500 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              All Time
            </button>
          </div>

          {/* Mode Switcher */}
          <select
            value={leadMode}
            onChange={(e) => setLeadMode(e.target.value as any)}
            className="text-xs font-medium px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-hidden"
          >
            <option value="REAL">Real Inquiries</option>
            <option value="ALL">All (Real + Test)</option>
          </select>

          {/* Export CSV */}
          <Button
            onClick={handleExportReportCsv}
            variant="outline"
            size="sm"
            className="text-xs h-8 flex items-center gap-1.5 border-slate-200 dark:border-slate-700"
          >
            <Download className="w-3.5 h-3.5 text-slate-500" />
            Export CSV
          </Button>

          {/* Print PDF */}
          <Button
            onClick={handlePrint}
            variant="outline"
            size="sm"
            className="text-xs h-8 flex items-center gap-1.5 border-slate-200 dark:border-slate-700"
          >
            <Printer className="w-3.5 h-3.5 text-slate-500" />
            Print / PDF
          </Button>

          {/* Refresh */}
          <button
            onClick={fetchReports}
            className="p-1.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 transition-colors"
            title="Refresh Report Data"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-primary" : ""}`} />
          </button>
        </div>
      </div>

      {/* 1. Core Financial & Pipeline KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Active Pipeline Value */}
        <div className="p-4 rounded-2xl bg-linear-to-br from-blue-50 to-indigo-50/40 dark:from-slate-900 dark:to-blue-950/20 border border-blue-200/60 dark:border-blue-900/40">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-400">
              Active Pipeline Value
            </span>
            <span className="p-2 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
              <IndianRupee className="w-4 h-4" />
            </span>
          </div>
          <p className="text-2xl font-black text-slate-900 dark:text-white mt-2">
            ₹{kpis.activePipelineValueINR.toLocaleString("en-IN")}
          </p>
          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
            <span className="font-bold text-blue-600 dark:text-blue-400">{kpis.activePipelineCount} Deals</span>
            <span>across active pipeline</span>
          </div>
        </div>

        {/* Won Revenue & Win Rate */}
        <div className="p-4 rounded-2xl bg-linear-to-br from-emerald-50 to-teal-50/40 dark:from-slate-900 dark:to-emerald-950/20 border border-emerald-200/60 dark:border-emerald-900/40">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
              Closed Won Revenue
            </span>
            <span className="p-2 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <Award className="w-4 h-4" />
            </span>
          </div>
          <p className="text-2xl font-black text-slate-900 dark:text-white mt-2">
            ₹{kpis.wonRevenueINR.toLocaleString("en-IN")}
          </p>
          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
            <span className="font-bold text-emerald-600 dark:text-emerald-400">{kpis.wonCount} Won</span>
            <span>• Win Rate: <strong>{kpis.winRate}%</strong></span>
          </div>
        </div>

        {/* Closed Lost & Loss Rate */}
        <div className="p-4 rounded-2xl bg-linear-to-br from-rose-50 to-slate-50 dark:from-slate-900 dark:to-rose-950/20 border border-rose-200/60 dark:border-rose-900/40">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-400">
              Closed Lost Opportunity
            </span>
            <span className="p-2 rounded-xl bg-rose-500/10 text-rose-600 dark:text-rose-400">
              <XCircle className="w-4 h-4" />
            </span>
          </div>
          <p className="text-2xl font-black text-slate-900 dark:text-white mt-2">
            ₹{kpis.lostValueINR.toLocaleString("en-IN")}
          </p>
          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
            <span className="font-bold text-rose-600 dark:text-rose-400">{kpis.lostCount} Lost</span>
            <span>• Loss Rate: <strong>{kpis.lossRate}%</strong></span>
          </div>
        </div>

        {/* Projected Monthly Retainer MRR */}
        <div className="p-4 rounded-2xl bg-linear-to-br from-purple-50 to-fuchsia-50/40 dark:from-slate-900 dark:to-purple-950/20 border border-purple-200/60 dark:border-purple-900/40">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-purple-700 dark:text-purple-400">
              Projected Monthly Retainer
            </span>
            <span className="p-2 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400">
              <Zap className="w-4 h-4" />
            </span>
          </div>
          <p className="text-2xl font-black text-slate-900 dark:text-white mt-2">
            ₹{kpis.projectedMonthlyRetainerINR.toLocaleString("en-IN")} <span className="text-xs font-normal text-slate-400">/mo</span>
          </p>
          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
            <span>Avg Deal: <strong>₹{kpis.avgDealSizeINR.toLocaleString("en-IN")}</strong></span>
          </div>
        </div>
      </div>

      {/* 2. Pipeline Funnel Conversion Chart */}
      <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Layers className="w-4 h-4 text-indigo-500" />
              Stage-by-Stage Sales Conversion Funnel
            </h3>
            <p className="text-xs text-slate-500">
              Progression velocity and drop-off rate from Google Maps scraping intake down to closed deal.
            </p>
          </div>
          <div className="text-right">
            <span className="text-xs text-slate-400">Gross Total Inquiries:</span>
            <span className="ml-1.5 text-xs font-bold text-slate-900 dark:text-white">{kpis.totalLeads} Leads</span>
          </div>
        </div>

        {/* Visual Funnel Stack */}
        <div className="space-y-3">
          {report.funnel.map((item, idx) => {
            const maxVal = Math.max(...report.funnel.map(f => f.count), 1);
            const widthPct = Math.max(8, Math.round((item.count / maxVal) * 100));

            return (
              <div key={item.stage} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="font-semibold text-slate-800 dark:text-slate-200">{item.label}</span>
                    <span className="text-[11px] text-slate-400">({item.stage})</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-slate-900 dark:text-white">{item.count} deals</span>
                    <span className="text-slate-400">|</span>
                    <span className="font-medium text-slate-600 dark:text-slate-300">₹{item.value.toLocaleString("en-IN")}</span>
                    <span className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-[11px] font-bold text-slate-700 dark:text-slate-300">
                      {item.percentage}%
                    </span>
                  </div>
                </div>

                {/* Funnel Progress Bar */}
                <div className="w-full h-3.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden p-0.5">
                  <div
                    className="h-full rounded-full transition-all duration-700 ease-out flex items-center justify-end pr-1.5"
                    style={{
                      width: `${widthPct}%`,
                      backgroundColor: item.color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. CLOSED LOST INTELLIGENCE & AUDIT CONSOLE */}
      <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
          <div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <XCircle className="w-4 h-4 text-rose-500" />
              Closed Lost Deals Intelligence &amp; Reactivation Console
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400">
                {report.lostDeals.length} Lost
              </span>
            </h3>
            <p className="text-xs text-slate-500">
              Examine why leads declined, review client replies, and reactivate cold opportunities with 1-click restore.
            </p>
          </div>

          <div className="text-xs text-slate-400">
            Total Lost Pipeline: <strong className="text-rose-600 dark:text-rose-400">₹{kpis.lostValueINR.toLocaleString("en-IN")}</strong>
          </div>
        </div>

        {report.lostDeals.length === 0 ? (
          <div className="p-8 text-center bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
            <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
            <p className="text-xs font-bold text-slate-700 dark:text-slate-300">Zero Lost Deals Currently!</p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              When a prospect replies with &quot;not interested&quot; or is marked lost, they will be listed here with diagnosis and re-engagement tools.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 uppercase tracking-wider font-semibold border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="py-2.5 px-3">Lead / Company</th>
                  <th className="py-2.5 px-3">Contact</th>
                  <th className="py-2.5 px-3">Lost Deal Value</th>
                  <th className="py-2.5 px-3">Detected Reason</th>
                  <th className="py-2.5 px-3">Lost Date</th>
                  <th className="py-2.5 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-medium">
                {report.lostDeals.map((lead) => (
                  <tr key={lead.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="py-3 px-3">
                      <div className="font-bold text-slate-900 dark:text-white">
                        {lead.company_name || lead.name}
                      </div>
                      <div className="text-[11px] text-slate-400">ID #{lead.id} • {lead.name}</div>
                    </td>
                    <td className="py-3 px-3">
                      <div className="font-mono text-[11px] text-slate-700 dark:text-slate-300">
                        {lead.phone || "-"}
                      </div>
                      <div className="text-[11px] text-slate-400 truncate max-w-[140px]">
                        {lead.email || "-"}
                      </div>
                    </td>
                    <td className="py-3 px-3 font-bold text-slate-900 dark:text-white">
                      ₹{lead.deal_value.toLocaleString("en-IN")}
                    </td>
                    <td className="py-3 px-3">
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-200 dark:border-rose-900">
                        {lead.last_reason}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-slate-500 text-[11px]">
                      {lead.lost_at ? new Date(lead.lost_at).toLocaleDateString("en-IN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}
                    </td>
                    <td className="py-3 px-3 text-right space-x-1.5">
                      {lead.phone && (
                        <a
                          href={`https://wa.me/${lead.phone.replace(/\D/g, "")}?text=${encodeURIComponent("Hi, Johnson here from Johnson Softwares. Following up regarding your real estate CRM requirements.")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/50 dark:hover:bg-emerald-900/60 text-emerald-700 dark:text-emerald-400 text-[11px] font-bold border border-emerald-200 dark:border-emerald-800 transition-colors"
                        >
                          <MessageSquare className="w-3 h-3" />
                          WhatsApp
                        </a>
                      )}
                      <Button
                        onClick={() => handleRestoreLead(lead.id)}
                        disabled={restoringId === lead.id}
                        variant="outline"
                        size="sm"
                        className="text-[11px] h-7 px-2.5 font-bold border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950"
                      >
                        <RotateCcw className={`w-3 h-3 mr-1 ${restoringId === lead.id ? "animate-spin" : ""}`} />
                        Restore to Pipeline
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 4. Channel Attribution Matrix & Product Mix */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Source Channels */}
        <div className="lg:col-span-2 p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-cyan-500" />
            Marketing &amp; Outreach Attribution Matrix
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 uppercase tracking-wider font-semibold border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="py-2 px-3">Source Channel</th>
                  <th className="py-2 px-3">Total Leads</th>
                  <th className="py-2 px-3">Won</th>
                  <th className="py-2 px-3">Lost</th>
                  <th className="py-2 px-3">Active</th>
                  <th className="py-2 px-3">Pipeline Value</th>
                  <th className="py-2 px-3">Win Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-medium">
                {report.sources.map((src, i) => (
                  <tr key={i} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                    <td className="py-2.5 px-3 font-bold text-slate-800 dark:text-slate-200">
                      {src.channel}
                    </td>
                    <td className="py-2.5 px-3 text-slate-700 dark:text-slate-300">{src.totalLeads}</td>
                    <td className="py-2.5 px-3 text-emerald-600 font-bold">{src.wonLeads}</td>
                    <td className="py-2.5 px-3 text-rose-500 font-bold">{src.lostLeads}</td>
                    <td className="py-2.5 px-3 text-blue-600 font-bold">{src.activeLeads}</td>
                    <td className="py-2.5 px-3 text-slate-900 dark:text-white font-semibold">
                      ₹{src.pipelineValue.toLocaleString("en-IN")}
                    </td>
                    <td className="py-2.5 px-3 font-bold text-slate-800 dark:text-slate-200">
                      {src.conversionRate}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Product / Tier Mix */}
        <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-1">
              <Award className="w-4 h-4 text-amber-500" />
              Real Estate Package Mix
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Breakdown of 3-Tier real estate solutions across inquiry pipeline.
            </p>

            <div className="space-y-3">
              {report.tierMix.map((tier) => (
                <div key={tier.tier} className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-slate-900 dark:text-white">{tier.name}</span>
                    <span className="px-1.5 py-0.5 rounded-md text-[10px] font-black bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                      {tier.tier}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs mt-2 text-slate-500">
                    <span>Est. Deals: <strong>{tier.count}</strong></span>
                    <span>Pipeline: <strong className="text-slate-900 dark:text-white">₹{tier.value.toLocaleString("en-IN")}</strong></span>
                  </div>
                  <div className="text-[11px] text-indigo-600 dark:text-indigo-400 mt-1 font-medium">
                    MRR Potential: ₹{tier.retainerValue.toLocaleString("en-IN")} / mo
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 p-3 rounded-xl bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200/60 dark:border-indigo-800/60 text-xs text-indigo-900 dark:text-indigo-300">
            💡 <strong>Founder Tip:</strong> Professional Plan (₹9,999/mo + ₹29,999 setup) represents 65% of customer inquiries. Promoted as the sweet-spot for growing Indian sales teams and clinics.
          </div>
        </div>
      </div>
    </div>
  );
}
